/* ═══════════════════════════════════════════════════════════════
   The Designer — the export dialog.

   Current section or the whole Manual (all or selected sections),
   Clean or Marked, straight through the existing pipeline
   (exportManual.ts → prepareSpecsForExport → the shared engine).
   Web ships Word only (platform law); desktop may add PDF.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ProjectSpec } from '../../shared/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { isWeb } from '../lib/platform';
import { exportManualSpecs, type ExportFormat } from './exportManual';
import type { DesignerExportContext } from './useDesignerDoc';

function Seg({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
  label: string;
}) {
  return (
    <div className="dsg-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`dsg-seg-btn${value === o.key ? ' is-active' : ''}`}
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** The open section (preselected in "Current section" scope). */
  currentSpecId: string;
  /** Every spec in the project, CSI order. */
  specs: ProjectSpec[];
  exportCtx: DesignerExportContext | null;
  /** Marks only exist once a baseline exists. */
  hasBaseline: boolean;
  toast: (msg: string, isError?: boolean) => void;
}

export function ExportDialog({
  open,
  onClose,
  currentSpecId,
  specs,
  exportCtx,
  hasBaseline,
  toast,
}: ExportDialogProps) {
  const [scope, setScope] = React.useState<'section' | 'manual'>('section');
  const [marked, setMarked] = React.useState(false);
  const [format, setFormat] = React.useState<ExportFormat>('docx');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setScope('section');
      setMarked(false);
      setSelected(new Set(specs.map((s) => s.id)));
    }
  }, [open, specs]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const targets =
    scope === 'section'
      ? specs.filter((s) => s.id === currentSpecId)
      : specs.filter((s) => selected.has(s.id));

  const run = async () => {
    if (!exportCtx) {
      toast('Still loading the project — try again in a moment.', true);
      return;
    }
    if (!targets.length) {
      toast('Pick at least one section to export.', true);
      return;
    }
    setBusy(true);
    try {
      const res = await exportManualSpecs({
        specs: targets,
        ctx: {
          project: exportCtx.project as Record<string, unknown>,
          allSpecs: exportCtx.allSpecs,
          formas: exportCtx.formas,
          client: exportCtx.client,
          team: exportCtx.team,
        },
        marked: marked && hasBaseline,
        format,
        isWeb,
      });
      if (res.kind === 'blocked') {
        toast(
          `Set ${res.placeholders.join(', ')} in the forma's Project Tokens before exporting.`,
          true
        );
        return;
      }
      if (res.kind === 'cancelled') return;
      if (res.failures.length) {
        toast(
          `Exported with ${res.failures.length} failure${res.failures.length === 1 ? '' : 's'} — see console.`,
          true
        );
        console.warn('[designer-export] failures:', res.failures);
      } else {
        toast(
          isWeb
            ? `Downloaded ${res.ok} file${res.ok === 1 ? '' : 's'}`
            : `Exported ${res.ok} file${res.ok === 1 ? '' : 's'} to ${res.outputDir}`
        );
        onClose();
      }
    } catch (err) {
      toast(
        'Export failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown')),
        true
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg dsg-desk" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Export</DialogTitle>
        </DialogHeader>

        <div className="dsg-export-body">
          <div className="dsg-desk-field">
            <span className="dsg-desk-label">What</span>
            <Seg
              label="Export scope"
              value={scope}
              onChange={(k) => setScope(k as 'section' | 'manual')}
              options={[
                { key: 'section', label: 'This section' },
                { key: 'manual', label: 'Whole manual' },
              ]}
            />
          </div>

          {scope === 'manual' && (
            <div className="dsg-desk-manifest" role="group" aria-label="Sections to export">
              {specs.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={selected.has(s.id)}
                  className="dsg-desk-manifest-row"
                  onClick={() => toggle(s.id)}
                >
                  <span
                    className={`dsg-rsp-box${selected.has(s.id) ? ' is-on' : ''}`}
                    aria-hidden="true"
                  >
                    {selected.has(s.id) && (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </span>
                  <span className="dsg-desk-manifest-csi">{s.csiNumber || '—'}</span>
                  <span className="dsg-desk-manifest-title">{s.title || 'Untitled section'}</span>
                </button>
              ))}
            </div>
          )}

          <div className="dsg-desk-field">
            <span className="dsg-desk-label">Changes</span>
            <Seg
              label="Clean or marked"
              value={marked ? 'marked' : 'clean'}
              onChange={(k) => setMarked(k === 'marked')}
              options={[
                { key: 'clean', label: 'Clean' },
                { key: 'marked', label: 'Mark changes' },
              ]}
            />
            {marked && !hasBaseline && (
              <span className="dsg-export-hint">
                No issuance yet — there is no baseline to mark against, so this exports clean.
              </span>
            )}
          </div>

          <div className="dsg-desk-field">
            <span className="dsg-desk-label">Format</span>
            {isWeb ? (
              <span className="dsg-export-hint">
                Word (.docx) — PDF export uses your installed fonts and runs in the desktop app.
              </span>
            ) : (
              <Seg
                label="File format"
                value={format}
                onChange={(k) => setFormat(k as ExportFormat)}
                options={[
                  { key: 'docx', label: 'Word' },
                  { key: 'pdf', label: 'PDF' },
                  { key: 'both', label: 'Both' },
                ]}
              />
            )}
          </div>
        </div>

        <div className="dsg-desk-actions">
          <button type="button" className="dsg-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="dsg-primary" onClick={() => void run()} disabled={busy}>
            {busy
              ? 'Exporting…'
              : `Export ${targets.length} section${targets.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
